import {useEffect, useState} from '@wordpress/element';
import {useBlockProps, useInnerBlocksProps, InspectorControls} from '@wordpress/block-editor';
import {useSelect} from '@wordpress/data';
import {TextControl, PanelBody, ToggleControl} from '@wordpress/components';
import {__} from '@wordpress/i18n';
import {buildCreditProperties, hasInnerBlocksContent} from "./tools";
import VideoPreview from "./components/videoPreview";
import HelpPopover from "./components/helpPopover";
import useVideoThumbnails from "./hooks/useVideoThumbnails";

export default function Edit({ attributes, setAttributes, clientId }) {
    const {
        videoUrl = '',
        videoThumbnails,
        videoTitle,
        credit,
        hasCaption,
        hasCreditOverlap,
        backgroundColor,
        style
    } = attributes;

    const blockProps = useBlockProps();
    const captionBlocksProps = useInnerBlocksProps(
        { className: 'simple-video-preview__caption' },
        {
            allowedBlocks: ['core/paragraph', 'core/list', 'core/details'],
            template: [
                ['core/paragraph', { placeholder: __('Add a legend for the video...', 'simple-video-preview') }]
            ]
        }
    );

    const innerBlocks = useSelect(select =>
        select('core/block-editor').getBlock(clientId)?.innerBlocks
    );

    useEffect(() => {
        const hasContent = hasInnerBlocksContent(innerBlocks);
        if (hasContent !== hasCaption) {
            setAttributes({ hasCaption: hasContent });
        }
    }, [innerBlocks, hasCaption, setAttributes]);

    const handleThumbnailsLoaded = thumbnailsData => {
        setAttributes({ videoThumbnails: thumbnailsData });
    };

    const creditProps = buildCreditProperties({
        hasCreditOverlap,
        backgroundColor,
        style,
        blockProps
    });

    const { error, isLoading } = useVideoThumbnails(videoUrl, handleThumbnailsLoaded);

    const parentBlockProps = { ...blockProps };
    delete parentBlockProps.style;

    return (
        <div {...parentBlockProps}>
            <InspectorControls>
                <PanelBody
                    title={__('Simple Video Preview Settings', 'simple-video-preview')}
                    initialOpen={true}
                >
                    <TextControl
                        label={__('Video URL', 'simple-video-preview')}
                        value={videoUrl}
                        onChange={newVideoUrl => setAttributes({ videoUrl: newVideoUrl })}
                    />
                    <TextControl
                        label={__('Video Title', 'simple-video-preview')}
                        value={videoTitle}
                        onChange={newVideoTitle => setAttributes({ videoTitle: newVideoTitle })}
                    />
                    <TextControl
                        label={__('Credit', 'simple-video-preview')}
                        value={credit}
                        onChange={newCredit => setAttributes({ credit: newCredit })}
                    />
                    <ToggleControl
                        __nextHasNoMarginBottom
                        label={__('Overlapping credit', 'simple-video-preview')}
                        help={hasCreditOverlap
                            ? __('Credit is overlapping preview.', 'simple-video-preview')
                            : __('Credit is above preview.', 'simple-video-preview')
                        }
                        checked={hasCreditOverlap}
                        onChange={newHasCreditOverlap => setAttributes({ hasCreditOverlap: newHasCreditOverlap })}
                    />
                    <HelpPopover/>
                </PanelBody>
            </InspectorControls>

            {error && (
                <div className="simple-video-preview__error">{error}</div>
            )}

            {isLoading && (
                <span className="simple-video-preview__loading">{__('Loading...', 'simple-video-preview')}</span>
            )}

            {videoUrl ? (
                <VideoPreview
                    videoThumbnails={videoThumbnails}
                    videoTitle={videoTitle}
                    credit={credit}
                    creditProps={creditProps}
                >
                    <figcaption {...captionBlocksProps} />
                </VideoPreview>
            ) : (
                <span className="simple-video-preview__container simple-video-preview__container-empty" />
            )}
        </div>
    );
}
